import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { leadStatusLabel } from '@/services/dialer/leadStatus';
import { formatDate } from '@/i18n/locale';

interface IncomingCallLike {
  parameters?: { From?: string };
  // Annotations posees par TwilioDeviceContext quand le voice-handler nous a
  // passe des Parameters custom (prospectName / prospectPhone).
  __displayName?: string;
  __displayPhone?: string;
  /** The prospect's list, from the server: known even when the loaded queue is another list. */
  __displayList?: string;
}

interface ProspectLite {
  prospect_name?: string | null;
  prospect_phone?: string | null;
  customer_id?: string | null;
  // #99 — le toast doit porter l'identite du contact qui appelle, pas
  // seulement son nom. Ces champs existent deja sur ProspectQueue ; il ne
  // manquait que de les declarer et de les afficher.
  prospect_company?: string | null;
  prospect_title?: string | null;
  /** The list (import source) the prospect belongs to. */
  source?: string | null;
  prospect_email?: string | null;
  linkedin_url?: string | null;
  lead_status?: string | null;
  score?: number | null;
  call_count?: number | null;
  last_call_at?: string | null;
  last_call_status?: string | null;
}

interface CustomerLite {
  id: string;
  company_name?: string | null;
  name?: string | null;
}

interface IncomingCallToastProps {
  incomingCall?: IncomingCallLike | null;
  prospectQueue?: ProspectLite[];
  customers?: CustomerLite[];
  onAccept: () => void;
  onReject: () => void;
  /**
   * Click sur le corps du toast (en dehors des boutons Accept/Reject) : ouvre
   * la CallSheet en mode preview pour que l'agent voie tous les details du
   * prospect avant de decider de decrocher.
   */
  onOpenPreview?: () => void;
}

export default function IncomingCallToast({
  incomingCall,
  prospectQueue,
  customers,
  onAccept,
  onReject,
  onOpenPreview,
}: IncomingCallToastProps) {
  const { t } = useTranslation('dialer');
  // Prefer le numero propre injecte par TwilioDeviceContext (custom Parameter
  // prospectPhone du TwiML) ; sinon fallback sur le From brut qui peut etre du
  // SIP URI "sip:+33...@sip.zadarma.com" (visuellement = email).
  const fromNumber = incomingCall?.__displayPhone || incomingCall?.parameters?.From || '';
  const fromDigits = fromNumber.replace(/\D/g, '');
  // Affichage : si fromNumber est un SIP URI, on extrait le E.164.
  const displayPhone = (() => {
    if (!fromNumber) return '';
    const sipMatch = fromNumber.match(/^sip:(\+?\d+)@/i);
    const captured = sipMatch?.[1];
    if (captured) return captured.startsWith('+') ? captured : `+${captured}`;
    return fromNumber;
  })();

  const matchedProspect = useMemo(() => {
    if (!incomingCall || !prospectQueue || !fromDigits) return null;
    // Comparaison digits-only des deux cotes pour tolerer les formats varies
    // ("+33616266395", "0616266395", "06 16 26 63 95", "33616266395"...).
    // On compare sur les 9 derniers chiffres (numero national sans prefixe).
    const fromSuffix = fromDigits.slice(-9);
    return (
      prospectQueue.find((p) => {
        if (!p.prospect_phone) return false;
        const pp = p.prospect_phone.replace(/\D/g, '');
        return pp.slice(-9) === fromSuffix;
      }) || null
    );
  }, [incomingCall, prospectQueue, fromDigits]);

  // Resolve le nom du client (Customer) lie au prospect matche. Si pas de
  // match prospect → on n'affiche pas la ligne client (cf. choix UX).
  const customerName = useMemo(() => {
    if (!matchedProspect?.customer_id || !customers) return null;
    const c = customers.find((c) => c.id === matchedProspect.customer_id);
    return c?.company_name || c?.name || null;
  }, [matchedProspect, customers]);

  const displayName = incomingCall?.__displayName || matchedProspect?.prospect_name || t('dialer:incoming.title');
  const listName = matchedProspect?.source || incomingCall?.__displayList || '';

  // #99 — "entreprise" au sens du client = celle du CONTACT qui appelle. Le
  // toast affichait `customerName`, c'est-a-dire le Customer du tenant
  // (mesure : "Devapps4biz" alors que le prospect travaille chez "Qonto").
  const entreprise = matchedProspect?.prospect_company || customerName || null;

  const dateCourte = (v?: string | null) => formatDate(v, { day: '2-digit', month: '2-digit', year: '2-digit' }) || null;
  const nbAppels = Number(matchedProspect?.call_count ?? 0);
  const dernierAppel = dateCourte(matchedProspect?.last_call_at);

  return (
    <AnimatePresence>
      {incomingCall && (
        <motion.div
          key="incoming-toast"
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 360, damping: 28 }}
          className="fixed z-[7500] pointer-events-auto"
          style={{
            right: 16,
            // #99 — le toast etait en bottom:16 et recouvrait la barre d'appel
            // du bas (chevauchement mesure : 360 x 32 px). Meme famille de
            // defaut que #10. La barre fait 48 px depuis #8 : on se pose
            // au-dessus.
            bottom: 64,
            // #99 — 320 -> 360 px : le toast porte desormais l'identite du
            // contact, pas seulement son nom.
            width: 360,
          }}
          role="dialog"
          aria-label={t('dialer:incoming.title')}
        >
          <div
            className="rounded-2xl shadow-2xl overflow-hidden"
            style={{
              background: '#111216',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {/* Body clickable : ouvre la preview CallSheet (sauf si on clique
                sur les boutons Accept/Reject). */}
            <div
              className="flex items-center gap-3 px-3.5 py-3 cursor-pointer transition-colors hover:bg-white/5"
              onClick={() => onOpenPreview?.()}
              role={onOpenPreview ? 'button' : undefined}
              tabIndex={onOpenPreview ? 0 : undefined}
              onKeyDown={(e) => {
                if (onOpenPreview && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onOpenPreview();
                }
              }}
            >
              <div className="relative flex-shrink-0">
                <span
                  className="absolute inset-0 rounded-full animate-ping"
                  style={{ background: 'rgba(52, 211, 153, 0.35)', animationDuration: '1.6s' }}
                />
                <span
                  className="relative flex items-center justify-center rounded-full"
                  style={{
                    width: 38,
                    height: 38,
                    background: 'rgba(52, 211, 153, 0.18)',
                    color: '#34D399',
                  }}
                >
                  <Phone className="w-4 h-4" />
                </span>
              </div>

              <div className="flex-1 min-w-0" data-testid="appel-entrant-infos">
                <p className="text-[13px] font-semibold text-white truncate">{displayName}</p>
                {/* Under the name: the prospect's list, then company and
                    position, so the agent knows which campaign is calling back. */}
                {listName && (
                  <p className="text-[11px] text-white/80 truncate" data-testid="appel-entrant-liste">{listName}</p>
                )}
                {(entreprise || matchedProspect?.prospect_title) && (
                  <p className="text-[11px] text-white/70 truncate">
                    {[entreprise, matchedProspect?.prospect_title].filter(Boolean).join(' · ')}
                  </p>
                )}
                <p className="text-[11px] text-white/50 truncate font-mono">
                  {displayPhone || t('dialer:incoming.unknownNumber')}
                </p>

                {/* #99 — statut, score et nombre d'appels precedents. Trois
                    puces courtes : dans une notification, l'agent lit en une
                    seconde ou il ne lit pas. */}
                {matchedProspect && (
                  <div className="flex flex-wrap items-center gap-1 mt-1" data-testid="appel-entrant-puces">
                    {matchedProspect.lead_status && (
                      <span className="text-[9.5px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.12)', color: '#E7EEEA' }}>
                        {leadStatusLabel(matchedProspect.lead_status, t)}
                      </span>
                    )}
                    <span className="text-[9.5px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.12)', color: '#E7EEEA' }}>
                      {t('dialer:incoming.previousCalls', { count: nbAppels })}
                    </span>
                  </div>
                )}

                {/* #99 — historique d'activite, en resume. Un toast de 360 px
                    ne loge pas un journal : on donne le dernier appel et son
                    issue, et le corps reste cliquable pour ouvrir la fiche
                    complete. */}
                {matchedProspect && (dernierAppel || matchedProspect.last_call_status) && (
                  <p className="text-[10px] text-white/45 truncate mt-0.5" data-testid="appel-entrant-historique">
                    {t('dialer:incoming.lastCall', { date: dernierAppel || '-' })}
                    {matchedProspect.last_call_status ? ` · ${outcomeLabel(matchedProspect.last_call_status, t)}` : ''}
                  </p>
                )}

                {/* #99 — email et LinkedIn "si disponibles" : on n'affiche pas
                    de ligne vide quand la donnee manque. */}
                {(matchedProspect?.prospect_email || matchedProspect?.linkedin_url) && (
                  <div className="flex items-center gap-2 mt-0.5" data-testid="appel-entrant-liens">
                    {matchedProspect?.prospect_email && (
                      <a
                        href={`mailto:${matchedProspect.prospect_email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] text-white/55 truncate hover:text-white"
                        style={{ maxWidth: 150 }}
                      >
                        {matchedProspect.prospect_email}
                      </a>
                    )}
                    {matchedProspect?.linkedin_url && (
                      <a
                        href={matchedProspect.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] font-semibold text-[#7CB9F5] hover:text-white shrink-0"
                      >
                        LinkedIn
                      </a>
                    )}
                  </div>
                )}

                <p className="text-[10px] text-emerald-400 font-medium mt-0.5">{t('dialer:incoming.ringing')}</p>
              </div>

              <button
                onClick={(e) => { e.stopPropagation(); onReject(); }}
                title={t('dialer:incoming.reject')}
                aria-label={t('dialer:incoming.rejectCall')}
                className="flex-shrink-0 inline-flex items-center justify-center rounded-full transition-transform active:scale-95"
                style={{
                  width: 38,
                  height: 38,
                  background: '#E5484D',
                  color: 'white',
                }}
              >
                <PhoneOff className="w-4 h-4" />
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); onAccept(); }}
                title={t('dialer:incoming.accept')}
                aria-label={t('dialer:incoming.acceptCall')}
                className="flex-shrink-0 inline-flex items-center justify-center rounded-full transition-transform active:scale-95"
                style={{
                  width: 38,
                  height: 38,
                  background: '#22C55E',
                  color: 'white',
                  boxShadow: '0 0 0 3px rgba(34,197,94,0.25)',
                }}
              >
                <Phone className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
